import {
  checkAnswer,
  type MiniManifest,
  type MiniState,
  type AnswerKey,
  type Exercise,
  type Answer,
} from './contract.js';
export function initialState(): MiniState {
  return {
    started: false,
    phase: 'introduction',
    cursor: null,
    results: {},
    draft: null,
    lastFeedback: null,
  };
}
export function start(state: MiniState): MiniState {
  return state.started ? state : { ...state, started: true, phase: 'first-pass' };
}
export function currentExercise(
  manifest: MiniManifest,
  state: MiniState,
  unlocked: string[],
): Exercise | null {
  if (!state.started || state.phase === 'completed') return null;
  const all = manifest.stages.flatMap((s) => s.exercises);
  const accessible = manifest.stages
    .filter((stage) => unlocked.includes(stage.id))
    .flatMap((stage) => stage.exercises);
  const pending = accessible.find((e) => !state.results[e.id]);
  if (pending) return pending;
  const failed = accessible.filter((e) => !state.results[e.id]?.correct);
  if (!failed.length) return null;
  const cursor = all.findIndex((e) => e.id === state.cursor);
  return failed.find((e) => all.indexOf(e) > cursor) ?? failed[0] ?? null;
}
export function grade(
  ex: Exercise,
  key: AnswerKey[string],
  answer: Answer,
): { correct: boolean; feedback: string } {
  checkAnswer(ex, answer);
  const correct =
    ex.kind === 'choice'
      ? answer === key.answer
      : ex.kind === 'order'
        ? JSON.stringify(answer) === JSON.stringify(key.answer)
        : ex.items.every(
            (i) =>
              (answer as Record<string, string>)[i.id] ===
              (key.answer as Record<string, string>)[i.id],
          );
  return { correct, feedback: correct ? key.explanation : key.hint };
}
export function submit(
  manifest: MiniManifest,
  keys: AnswerKey,
  state: MiniState,
  exerciseId: string,
  answer: Answer,
  unlocked: string[],
): MiniState {
  const ex = currentExercise(manifest, state, unlocked);
  if (!ex || ex.id !== exerciseId)
    throw new Error('Cet exercice n’est pas disponible à cette étape.');
  const key = keys[ex.id];
  if (!key) throw new Error('Le corrigé de cet exercice est indisponible.');
  const result = grade(ex, key, answer);
  const previous = state.results[ex.id];
  const results = {
    ...state.results,
    [ex.id]: {
      attempts: (previous?.attempts ?? 0) + 1,
      firstCorrect: previous?.firstCorrect ?? result.correct,
      correct: result.correct,
      lastAnswer: answer,
      feedback: result.feedback,
    },
  };
  const all = manifest.stages.flatMap((s) => s.exercises);
  const phase = all.every((e) => results[e.id]?.correct)
    ? 'completed'
    : manifest.stages
          .filter((stage) => unlocked.includes(stage.id))
          .flatMap((stage) => stage.exercises)
          .every((e) => results[e.id])
      ? 'review'
      : 'first-pass';
  return { ...state, results, phase, cursor: ex.id, draft: null, lastFeedback: result.feedback };
}
export function summarize(manifest: MiniManifest, state: MiniState) {
  const all = manifest.stages.flatMap((s) => s.exercises);
  const counts = (exercises: Exercise[]) => ({
    total: exercises.length,
    correct: exercises.filter((e) => state.results[e.id]?.correct).length,
    firstCorrect: exercises.filter((e) => state.results[e.id]?.firstCorrect).length,
    attempts: exercises.reduce((n, e) => n + (state.results[e.id]?.attempts ?? 0), 0),
  });
  return {
    ...counts(all),
    remaining: all
      .filter((e) => !state.results[e.id]?.correct)
      .map((e) => ({ id: e.id, title: e.title, attempted: Boolean(state.results[e.id]) })),
    skills: manifest.skills.map((s) => ({
      ...s,
      ...counts(all.filter((e) => e.skills.includes(s.id))),
    })),
  };
}
